import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProducts } from '../api/api';
import { useCart } from '../context/CartContext';
import { hasSale, getSalePrice, withEffectivePrice } from '../utils/sale';
import './Products.css';

function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { cartItems, addToCart } = useCart();
  const [filters, setFilters] = useState({
    category: '',
    culture: '',
    search: ''
  });
  
  // Restore scroll position and filters on mount
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('productsScroll');
    const savedFilters = sessionStorage.getItem('productsFilters');
    const savedPage = sessionStorage.getItem('productsPage');
    
    if (savedFilters) {
      try {
        setFilters(JSON.parse(savedFilters));
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    if (savedPage) {
      setPage(parseInt(savedPage));
    }
    
    // Restore scroll position after a short delay to ensure DOM is ready
    if (savedScroll) {
      const timer = setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);
  
  // Save scroll position and filters when navigating away
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('productsScroll', window.scrollY);
      sessionStorage.setItem('productsFilters', JSON.stringify(filters));
      sessionStorage.setItem('productsPage', page);
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [filters, page]);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await getProducts({ ...filters, page, limit: 20 });
      // Backend returns { products: [...], pagination: {...} }
      let products = response.data.products || [];
      
      // Additional frontend filter: Only show published products
      // This is a safety measure in case backend filter doesn't work
      products = products.filter(product => product.isPublished !== false);
      
      setProducts(products);
      
      // Get total pages from backend response
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
    setPage(1); // Reset to page 1 when filters change
  };

  // Helper function to get image URL
  const getImageUrl = (image) => {
    if (!image) return 'https://picsum.photos/seed/placeholder/500/500';
    const url = typeof image === 'string' ? image : image.url;
    if (!url) return 'https://picsum.photos/seed/placeholder/500/500';
    return url.startsWith('http') ? url : `${window.location.origin}/.netlify/functions/api${url}`;
  };

  // Get quantity of product in cart
  const getCartQuantity = (productId) => {
    const item = cartItems.find(item => item._id === productId);
    return item ? item.quantity : 0;
  };

  // Handle add to cart with click stop propagation
  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(withEffectivePrice(product), 1);
  };

  return (
    <div className="products-page container">
      <h1>Our Products</h1>
      
      <div className="filters">
        <input
          type="text"
          name="search"
          placeholder="Search products..."
          value={filters.search}
          onChange={handleFilterChange}
        />
        
        <select name="category" value={filters.category} onChange={handleFilterChange}>
          <option value="">All Categories</option>
          <option value="Traditional Attire">Traditional Attire</option>
          <option value="Accessories">Accessories</option>
          <option value="Jewelry">Jewelry</option>
          <option value="Headwear">Headwear</option>
          <option value="Footwear">Footwear</option>
          <option value="Home Décor">Home Décor</option>
          <option value="Ceremonial Items">Ceremonial Items</option>
          <option value="Traditional Art">Traditional Art</option>
          <option value="Paintings & Drawings">Paintings & Drawings</option>
          <option value="Sculptures & Carvings">Sculptures & Carvings</option>
          <option value="Beadwork">Beadwork</option>
          <option value="Pottery & Ceramics">Pottery & Ceramics</option>
          <option value="Textiles & Fabrics">Textiles & Fabrics</option>
          <option value="Musical Instruments">Musical Instruments</option>
          <option value="Cultural Books">Cultural Books</option>
          <option value="Educational Materials">Educational Materials</option>
          <option value="Traditional Weapons & Tools">Traditional Weapons & Tools</option>
          <option value="Handcrafted Items">Handcrafted Items</option>
          <option value="Wall Art & Prints">Wall Art & Prints</option>
        </select>
        
        <select name="culture" value={filters.culture} onChange={handleFilterChange}>
          <option value="">All Cultures</option>
          <option value="Zulu">Zulu</option>
          <option value="Xhosa">Xhosa</option>
          <option value="Venda">Venda</option>
          <option value="Tswana">Tswana</option>
          <option value="Nama (Khoekhoe)">Nama (Khoekhoe)</option>
          <option value="San (Haiom)">San (Haiom)</option>
          <option value="Sotho">Sotho</option>
          <option value="Ndebele">Ndebele</option>
          <option value="Swati">Swati</option>
          <option value="Tsonga">Tsonga</option>
          <option value="Pedi">Pedi</option>
          <option value="Afrikaans">Afrikaans</option>
          <option value="English">English</option>
          <option value="Multi-Cultural">Multi-Cultural</option>
          <option value="Pan-African">Pan-African</option>
        </select>
      </div>

      {loading ? (
        <p>Loading products...</p>
      ) : products.length === 0 ? (
        <p className="no-products">No products found.</p>
      ) : (
        <>
          <div className="product-grid">
            {products.map(product => {
              const cartQuantity = getCartQuantity(product._id);
              return (
                <Link to={`/products/${product._id}`} key={product._id} className="product-card">
                  <div className="product-card__image-wrapper">
                    <img 
                      src={getImageUrl(product.images && product.images[0])}
                      alt={product.name} 
                    />
                    {hasSale(product) && (
                      <span className="product-card__sale-badge">SALE {product.salePercentage}% OFF</span>
                    )}
                    {cartQuantity > 0 && (
                      <span className="product-card__cart-badge">{cartQuantity}</span>
                    )}
                  </div>
                  <h3>{product.name}</h3>
                  {product.sku && <p className="sku">SKU: {product.sku}</p>}
                  {hasSale(product) ? (
                    <p className="price">
                      <span className="price--original">R{(product.price || 0).toFixed(2)}</span>
                      {' '}
                      <span className="price--sale">R{getSalePrice(product).toFixed(2)}</span>
                    </p>
                  ) : (
                    <p className="price">R{(product.price || 0).toFixed(2)}</p>
                  )}
                  <p className="culture">{product.culturalOrigin || product.culture} | {product.category}</p>
                  <p className={`stock-badge ${product.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
                    {product.stock > 0 ? `${product.stock} in stock` : 'Out of Stock'}
                  </p>
                  <button
                    className="product-card__cart-btn"
                    onClick={(e) => handleAddToCart(e, product)}
                    title="Add to cart"
                  >
                    🛒
                  </button>
                </Link>
              );
            })}
          </div>
          
          {/* Pagination Controls */}
          <div className="pagination">
            <button 
              onClick={() => {
                setPage(Math.max(1, page - 1));
                window.scrollTo(0, 0);
              }}
              disabled={page === 1}
              className="pagination-btn"
            >
              ← Previous
            </button>
            
            <span className="pagination-info">Page {page} of {totalPages}</span>
            
            <button 
              onClick={() => {
                setPage(page + 1);
                window.scrollTo(0, 0);
              }}
              disabled={page >= totalPages}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Products;
